<template>
  <div class="group" :class="{ [sectionClasses]: true, hidden: mq.current === '' }">
    <div
      class="flex items-center justify-between"
      tabindex="0"
      :class="collapsableClasses"
      @mousedown="isPressed = true"
      @mouseup="isPressed = false"
      @mouseleave="isPressed = false"
      @touchstart="isPressed = true"
      @touchend="isPressed = false"
      @touchcancel="isPressed = false"
      @click="toggle"
      @keyup.enter="toggle"
    >
      <LinkWrapper
        v-if="!!titleUrl"
        :link-url="titleUrl"
        :is-external-link="titleUrlIsExternalLink"
        class="mb-0 underline text-primary-900"
        :class="titleClassesCombined"
      >
        {{ title }}
      </LinkWrapper>
      <CText v-else class="mb-0 text-primary-900" :class="titleClassesCombined">
        {{ title }}
      </CText>
      <div :class="{ hidden: !shouldRenderAsCollapsable }">
        <CIcon :name="isOpen ? 'expand_less' : 'expand_more'" />
      </div>
    </div>
    <component
      :is="collapsableContentAs"
      :class="[
        isOpen || !shouldRenderAsCollapsable ? 'block' : 'hidden',
        isOpen && shouldRenderAsCollapsable ? collapsableContentClasses : '',
      ]"
    >
      <slot />
    </component>
  </div>
</template>

<script setup lang="ts">
import { Brand } from '@/config/brand.config';
import { AllowedTags } from '~/types';

const config = useRuntimeConfig();

const isPressed = ref<boolean>(false);

const textClasses = 'text-xs text-sm text-base text-lg text-xl text-2xl text-3xl text-4xl text-5xl text-6xl';
const fontWeightClasses = 'font-light, font-normal, font-medium, font-semibold, font-bold, font-extrabold, font-black';

type Props = {
  title?: string;
  titleUrl?: string;
  titleUrlIsExternalLink?: boolean;
  collapsableScreenSizes?: ('xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge')[];
  isOpen?: boolean;
  collapsablePadding?: string;
  collapsableContentClasses?: string;
  titleClasses?: string;
  sectionClasses?: string;
  collapsableContentAs?: AllowedTags;
};

const props = withDefaults(defineProps<Props>(), {
  title: '',
  titleUrl: '',
  titleUrlIsExternalLink: false,
  collapsableScreenSizes: () => ['xsmall', 'small', 'medium', 'large', 'xlarge', 'xxlarge'],
  isOpen: false,
  collapsablePadding: 'px-6 py-4',
  collapsableContentClasses: 'px-6 py-4',
  titleClasses: '',
  sectionClasses: '',
  collapsableContentAs: 'div',
});

const emit = defineEmits<{ (e: 'update:open', value: boolean): void }>();

const { mq } = useMediaQueries();

const shouldRenderAsCollapsable = computed(() =>
  props.collapsableScreenSizes.includes(
    mq.value.current as 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge',
  ),
);
const collapsableClasses = computed(() =>
  shouldRenderAsCollapsable.value
    ? `cursor-pointer ${isPressed.value ? 'bg-primary-100' : 'hover:bg-primary-50'} ${props.collapsablePadding}`
    : '',
);
const titleClassesCombined = computed(() => {
  const classes = props.titleClasses === '' ? [] : props.titleClasses.split(' ');

  if (classes.filter((cls) => textClasses.includes(cls)).length === 0) classes.push('text-base');
  if (classes.filter((cls) => fontWeightClasses.includes(cls)).length === 0)
    classes.push((config.public.brand as Brand) === 'opti' ? 'font-medium' : 'font-semibold');

  return classes.join(' ');
});

const toggle = () => emit('update:open', !props.isOpen);
</script>

<script lang="ts">
export default defineComponent({
  name: 'CollapsableSection',
});
</script>
